import { useParams, useSearch } from "@tanstack/react-router";
import { Award, BadgeCheck, Pencil } from "lucide-react";
import { useState } from "react";
import { CertificationFormDialog } from "@/components/certifications/CertificationFormDialog";
import { CertificationPeopleSection } from "@/components/certifications/CertificationPeopleSection";
import { CertificationRollup } from "@/components/certifications/CertificationRollup";
import { ActivityLog } from "@/components/shared/activity-log";
import { DetailSection, MetadataRow } from "@/components/shared/detail-section";
import { type DetailMetaItem, EntityDetailHeader } from "@/components/shared/entity-detail-header";
import { EntityDetailPage } from "@/components/shared/entity-detail-page";
import { LifecycleBadge } from "@/components/shared/lifecycle-badge";
import { TabBar, type TabItem } from "@/components/shared/tab-bar";
import { Button } from "@/components/ui/button";
import { useCertification } from "@/hooks/use-certifications";
import { useSession } from "@/lib/auth-client";
import { parseOriginSearch } from "@/lib/detail-origin";
import { canEditAll } from "@/lib/permissions";

type TabKey = "overview" | "people" | "history";

const TABS: TabItem[] = [
  { key: "overview", label: "Overview" },
  { key: "people", label: "People" },
  { key: "history", label: "Change history" },
];

function isTabKey(value: unknown): value is TabKey {
  return value === "overview" || value === "people" || value === "history";
}

export function CertificationDetailPage() {
  const params = useParams({ strict: false });
  const certificationId = typeof params.certificationId === "string" ? params.certificationId : "";
  const query = useCertification(certificationId);
  const { data: session } = useSession();
  const isAdmin = canEditAll(session?.user?.role);
  const [editing, setEditing] = useState(false);

  // `tab` lets a back-link from a person page reopen a specific tab (e.g. People).
  // The rest of the origin handling lives in EntityDetailPage; this is the same
  // pure read of the same search params.
  const parsed = parseOriginSearch(useSearch({ strict: false }));
  const [tab, setTab] = useState<TabKey>(() => (isTabKey(parsed.tab) ? parsed.tab : "overview"));
  // Reset the tab when navigating certification→certification (same route, no
  // remount) so it doesn't leak from the previous record. React re-renders
  // synchronously on this state set (documented reset-on-prop-change pattern).
  const [seenId, setSeenId] = useState(certificationId);
  if (certificationId !== seenId) {
    setSeenId(certificationId);
    setTab(isTabKey(parsed.tab) ? parsed.tab : "overview");
  }

  return (
    <EntityDetailPage
      query={query}
      entityLabel="Certification"
      section="Certifications"
      list={{ label: "Certifications", to: "/dashboard/certifications" }}
      name={query.data?.name}
    >
      {(certification) => {
        const meta: DetailMetaItem[] = [];
        if (certification.issuingBody)
          meta.push({ key: "issuer", icon: BadgeCheck, node: certification.issuingBody });

        return (
          <>
            <EntityDetailHeader
              visual={
                <span className="flex size-20 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
                  <Award className="size-8" />
                </span>
              }
              title={certification.name}
              subtitle={certification.description}
              meta={meta}
              badges={<LifecycleBadge status={certification.status} />}
              actions={
                isAdmin && (
                  <Button size="sm" onClick={() => setEditing(true)}>
                    <Pencil className="mr-1.5 size-3.5" />
                    Edit
                  </Button>
                )
              }
            />

            <TabBar tabs={TABS} value={tab} onChange={(k) => setTab(k as TabKey)} />

            {tab === "overview" && (
              <DetailSection title="Overview">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <MetadataRow label="Issuing body" value={certification.issuingBody} />
                  <MetadataRow label="Description" value={certification.description} />
                </div>
              </DetailSection>
            )}

            {tab === "people" && (
              <DetailSection title="People">
                <CertificationRollup certificationId={certification.id} />
                <CertificationPeopleSection
                  certificationId={certification.id}
                  certificationName={certification.name}
                />
              </DetailSection>
            )}

            {tab === "history" && (
              <DetailSection title="Change history">
                <ActivityLog
                  entityType="certification"
                  entityId={certification.id}
                  fieldLabels={{ issuingBody: "Issuing body" }}
                />
              </DetailSection>
            )}

            {isAdmin && (
              <CertificationFormDialog
                key={certification.updatedAt}
                open={editing}
                onOpenChange={setEditing}
                certification={certification}
              />
            )}
          </>
        );
      }}
    </EntityDetailPage>
  );
}
